/**
 * IntegrationsPanel - Third-party service connections
 * Handles: Stripe, Google Maps, and other API integrations
 */
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useIntegrationSettings } from '@/hooks/useIntegrationSettings';
import { GOOGLE_MAPS_API_KEY } from '@/config/constants';

// Stripe Direct Integration card
const StripeIntegrationCard = ({ model }) => {
  const { controller, integrations, loading, statusError, florists, selectedFloristId,
    mappingError, mappingLoading, mappingKnown, pending, active, outcome } = model;
  const stripeStatus = integrations?.stripe;
  const onRefresh = controller.refresh;
  const loadFloristMapping = controller.refreshMapping;
  const [showForm, setShowForm] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [armedRemoval, setArmedRemoval] = useState(false);
  const mounted = useRef(false);
  const opening = useRef(0);
  const copyVersion = useRef(0);
  const copyTimer = useRef(null);
  const savingRequest = useRef(false);
  const busy = pending || saving || !active;
  const webhookUrl = `${window.location.origin}/api/webhooks/stripe`;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      opening.current += 1;
      copyVersion.current += 1;
      clearTimeout(copyTimer.current);
    };
  }, []);
  useEffect(() => {
    if (!armedRemoval) return undefined;
    const timer = setTimeout(() => setArmedRemoval(false), 3000);
    return () => clearTimeout(timer);
  }, [armedRemoval]);
  const closeForm = () => {
    opening.current += 1;
    setShowForm(false);
    setApiKey('');
    setWebhookSecret('');
  };
  const toggleForm = () => {
    if (!controller.owns() || busy) return;
    opening.current += 1;
    setApiKey('');
    setWebhookSecret('');
    setShowForm(!showForm);
    setArmedRemoval(false);
  };
  const handleFloristChange = async e => {
    if (!controller.owns() || busy || !mappingKnown) return;
    setArmedRemoval(false);
    await controller.mutate('mapping', e.target.value);
  };
  const handleCopyUrl = async () => {
    if (!controller.owns()) return;
    const request = ++copyVersion.current;
    const current = () => mounted.current && controller.owns() && request === copyVersion.current;
    try {
      await navigator.clipboard.writeText(webhookUrl);
      if (!current()) return;
      setCopied(true);
      toast.success('Webhook URL copied!');
      clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => { if (current()) setCopied(false); }, 2000);
    } catch { if (current()) toast.error('Failed to copy'); }
  };
  const handleSave = async () => {
    if (!controller.owns() || busy || savingRequest.current || !apiKey.trim() || !webhookSecret.trim()) return;
    const owner = opening.current;
    savingRequest.current = true;
    setArmedRemoval(false);
    setSaving(true);
    const request = controller.mutate('credentials', { apiKey: apiKey.trim(), secret: webhookSecret.trim() });
    // The controller owns the current attempt; the form retains no retry payload.
    setApiKey('');
    setWebhookSecret('');
    const result = await request;
    if (!mounted.current || !controller.owns() || opening.current !== owner) return;
    savingRequest.current = false;
    setSaving(false);
    if (result.kind === 'accepted') closeForm();
  };
  const handleRemove = async () => {
    if (!controller.owns() || busy) return;
    if (!armedRemoval) { setArmedRemoval(true); return; }
    setArmedRemoval(false);
    await controller.mutate('remove');
  };
  const configuration = stripeStatus?.configuration;
  const savedLabels = { absent: 'Not saved', readable: 'Saved and readable', unreadable: 'Saved but unreadable', not_considered: 'Not considered by the current selection policy' };
  const sourceLabels = { saved: 'Saved settings', environment: 'Server configuration', none: 'None available' };
  const knownLabel = (labels, value, fallback) => typeof value === 'string'
    && Object.prototype.hasOwnProperty.call(labels, value) ? labels[value] : fallback;

  return (
    <Card className="border-stone-200" data-testid="integration-stripe">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center flex-shrink-0">
              <span className="font-bold text-sm">S</span>
            </div>
            <div>
              <h4 className="font-medium text-stone-900">Stripe</h4>
              <p className="text-xs text-stone-500 mb-2">
                Receive orders automatically from Stripe payments
              </p>
              {loading ? <p className="text-xs">Loading configuration...</p> : statusError ? (
                <p role="alert" className="text-xs text-amber-800">Unable to determine configuration. Previously displayed status may be stale.</p>
              ) : configuration ? (
                <ul className="text-xs space-y-2">
                  {[[ 'api_key', 'API key' ], [ 'webhook_secret', 'Signing secret' ]].map(([key, label]) => (
                    <li key={key} data-testid={`stripe-status-${key}`}>
                      <strong>{label}:</strong> {knownLabel(savedLabels, configuration[key]?.saved_state, 'Saved state unknown')}. Runtime source: {knownLabel(sourceLabels, configuration[key]?.effective_source, 'Unknown')}.
                    </li>
                  ))}
                </ul>
              ) : <p className="text-xs text-amber-800">This server cannot report effective Stripe configuration. It is unknown, not necessarily missing.</p>}
              <p className="text-xs text-stone-500 mt-2">Configuration has not been checked with Stripe.</p>
              <Button variant="ghost" size="sm" disabled={busy || loading} onClick={() => onRefresh()} data-testid="stripe-refresh">{statusError ? 'Retry configuration' : 'Refresh configuration'}</Button>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
              <Button size="sm" onClick={toggleForm} disabled={busy} className="h-8 text-xs" data-testid="setup-stripe">Edit credentials</Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRemove}
                disabled={busy}
                className="text-red-600 hover:bg-red-50 border-red-200 h-8 text-xs"
                data-testid="disconnect-stripe"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" />
                {armedRemoval ? 'Click again to confirm' : 'Remove saved settings'}
              </Button>
          </div>
        </div>
        <p className="mt-3 text-xs text-stone-500">Removing saved Stripe settings also removes the sending-florist default. Server-configured credentials are unaffected, so removal does not necessarily stop Stripe processing.</p>
        {outcome && <p role="status" className="mt-3 text-sm" data-testid="stripe-write-outcome">{outcome}</p>}

        {/* Webhook URL Helper - always visible */}
        <div className="mt-4 pt-4 border-t border-stone-100">
          <div className="space-y-1.5">
            <Label className="text-xs text-stone-600">Your Webhook URL</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-stone-100 px-3 py-2 rounded font-mono text-stone-700 truncate">
                {webhookUrl}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyUrl}
                className="h-8 text-xs flex-shrink-0"
                data-testid="copy-webhook-url"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <p className="text-[11px] text-stone-400">
              Add this URL in Stripe Dashboard → Developers → Webhooks → Add endpoint
            </p>
          </div>
        </div>

        {/* Sending Florist mapping — always visible (independent of API-key state) */}
        <div className="mt-4 pt-4 border-t border-stone-100">
            <div className="space-y-1.5">
              <Label className="text-xs text-stone-600" htmlFor="stripe-sending-florist-select">
                Default Sending Florist for Stripe Orders
              </Label>
              <select
                id="stripe-sending-florist-select"
                value={selectedFloristId}
                onChange={handleFloristChange}
                disabled={busy || mappingLoading || !mappingKnown}
                data-testid="stripe-sending-florist-select"
                className="w-full h-9 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:opacity-60"
              >
                <option value="">{mappingKnown ? '— None (no auto-mapping) —' : 'Default unknown'}</option>
                {florists.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
              {mappingError && <div role="alert" className="text-xs text-amber-800">Unable to determine the sending-florist default. <Button variant="ghost" size="sm" disabled={busy} onClick={loadFloristMapping}>Retry florist mapping</Button></div>}
              <p className="text-[11px] text-stone-400">
                Incoming Stripe webhook orders will be auto-tagged with this florist so the Designer Brief can resolve its product URL. Manage the directory in Settings → Sending Florists.
              </p>
            </div>
          </div>

        {/* Setup form */}
        {showForm && (
          <div className="mt-4 pt-4 border-t border-stone-100 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="stripe-api-key" className="text-xs">
                API Secret Key <span className="text-red-500">*</span>
              </Label>
              <Input
                id="stripe-api-key"
                type="password"
                disabled={busy}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="sk_test_... or sk_live_..."
                data-testid="stripe-api-key-input"
                className="font-mono text-sm"
              />
              <p className="text-[11px] text-stone-400">
                Stripe Dashboard → Developers → API Keys → Secret key
              </p>
            </div>
            
            <div className="space-y-1.5">
              <Label htmlFor="stripe-webhook-secret" className="text-xs">
                Webhook Signing Secret <span className="text-red-500">*</span>
              </Label>
              <Input
                id="stripe-webhook-secret"
                type="password"
                disabled={busy}
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                placeholder="whsec_..."
                data-testid="stripe-webhook-secret-input"
                className="font-mono text-sm"
              />
              <p className="text-[11px] text-stone-400">
                Stripe Dashboard → Developers → Webhooks → Select endpoint → Signing secret
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleSave}
                disabled={busy || !apiKey.trim() || !webhookSecret.trim()}
                className="h-8 text-xs"
                data-testid="stripe-save-btn"
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={closeForm}
                className="h-8 text-xs"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Presence only: the app build and connected backend have separate credentials.
const GoogleMapsConfigurationCard = ({ googleStatus, loading, statusError, onRefresh }) => {
  const configuration = !loading && !statusError ? googleStatus?.configuration : null;
  const presenceLabel = value => value === true ? 'Present' : value === false ? 'Not configured' : 'Unknown';
  return (
    <Card className="border-stone-200" data-testid="integration-google_maps">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
            <span className="font-bold text-sm">G</span>
          </div>
          <div>
            <h4 className="font-medium text-stone-900">Google Maps</h4>
            <p className="text-xs text-stone-500 mb-2">Maps, address suggestions and online routing</p>
            <ul className="text-xs space-y-2">
              <li data-testid="google-browser-status"><strong>Browser key (this app build):</strong> {presenceLabel(Boolean(GOOGLE_MAPS_API_KEY))}.</li>
              <li data-testid="google-server-status"><strong>Server key (connected backend):</strong> {presenceLabel(configuration?.server_key_present)}.</li>
            </ul>
            {loading ? <p className="text-xs mt-2">Loading backend configuration...</p> : statusError ? (
              <p role="alert" className="text-xs text-amber-800 mt-2">Unable to determine backend configuration. Previously displayed status may be stale.</p>
            ) : typeof configuration?.server_key_present !== 'boolean' && (
              <p className="text-xs text-amber-800 mt-2">This server cannot report Google server-key presence. It is unknown, not necessarily missing.</p>
            )}
            <p className="text-xs text-stone-500 mt-2" data-testid="google-legacy-status">
              {configuration?.legacy_saved_key_present === true
                ? 'A legacy Google key is retained in saved settings, but is not used by the current map, address or routing features.'
                : configuration?.legacy_saved_key_present === false
                  ? 'No legacy Google key is saved.'
                  : 'Legacy saved-key presence is unknown.'}
            </p>
            <p className="text-xs text-stone-500 mt-2">Keys are managed through app-build and server configuration, not this screen. Configuration has not been checked with Google; key presence does not verify permissions, billing or availability.</p>
            <Button variant="ghost" size="sm" disabled={loading} onClick={() => onRefresh()} data-testid="google-refresh">{statusError ? 'Retry configuration' : 'Refresh configuration'}</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};


const IntegrationsPanel = () => {
  const model = useIntegrationSettings();
  return (
    <div className="space-y-4">
      {model.connectionError && <p role="alert">The backend connection changed. Reload before managing integrations.</p>}
      {!model.active && !model.connectionError && <p role="alert">Integration settings are unavailable for this session.</p>}
      {model.pending && <p role="status">A settings operation is still settling. Changes remain unavailable until it settles.</p>}
      <StripeIntegrationCard key={model.controller.key} model={model} />
      <GoogleMapsConfigurationCard
        googleStatus={model.integrations?.google_maps}
        loading={model.loading}
        statusError={model.statusError}
        onRefresh={model.controller.refresh}
      />
    </div>
  );
};

export default IntegrationsPanel;
